/**
 * Typed access to the support-chat bridge declared inline in index.html.
 *
 * The bridge itself must live in the HTML: onBitrixLiveChat fires once, when the
 * CRM site-button loader initialises the widget, which can happen before React
 * has mounted. A listener registered from React would miss it outright on fast
 * loads.
 */
import type { SupportChatIdentity } from '@aica/api-types';

interface SupportChatBridge {
  widget: unknown;
  identity: SupportChatIdentity | null;
  appliedKey: string | null;
  apply: () => void;
}

function getBridge(): SupportChatBridge | undefined {
  return (globalThis as { __aeloChat?: SupportChatBridge }).__aeloChat;
}

/**
 * Hand the identity to the widget, now or as soon as the widget shows up.
 * A missing bridge means the widget never loaded (blocked, offline) — that is
 * an ordinary outcome, not an error: support must keep working either way.
 *
 * `bridge.apply()` calls into the widget's own `setUserRegisterData` /
 * `setCustomData` — external, opaque code we do not control. This function is
 * the safe boundary the rest of the app relies on, so a throw from either must
 * never escape: swallow it rather than let it propagate into a React effect
 * with no isolation of its own.
 */
export function publishSupportChatIdentity(identity: SupportChatIdentity): void {
  const bridge = getBridge();
  if (!bridge) return;

  bridge.identity = identity;
  try {
    bridge.apply();
  } catch {
    // Widget internals threw — support must keep working regardless.
  }
}
